import {
	type PointerEvent as ReactPointerEvent,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
} from "react";

export type PointerDragHandlers = {
	/** Attach to the draggable element (e.g. `<div {...handlers} />`). */
	onPointerDown: (e: ReactPointerEvent) => void;
};

export type UsePointerDragArgs = {
	/** Called on pointerdown with the initial event. Return `false` to cancel. */
	onStart?: (e: ReactPointerEvent) => boolean | void;
	/** Called on each pointermove while captured. */
	onMove: (e: PointerEvent) => void;
	/** Called on pointerup / pointercancel. */
	onEnd?: (e: PointerEvent) => void;
};

type ActiveDrag = {
	pointerId: number;
	target: HTMLElement;
	onMove: (e: PointerEvent) => void;
	onEnd: (e: PointerEvent) => void;
};

/**
 * Generic Pointer Events drag helper.
 *
 * On `pointerdown` it calls `onStart` (which may cancel by returning `false`),
 * captures the pointer on `e.currentTarget`, then forwards `pointermove` and
 * `pointerup` / `pointercancel` events to `onMove` / `onEnd` — filtering by
 * the original pointerId so only one pointer drives the drag at a time.
 *
 * Pointer capture is ALWAYS released on end (even if `onEnd` throws) via a
 * `try/finally` and on unmount via a cleanup effect.
 *
 * Consumer elements MUST set `style={{ touchAction: "none" }}` so the browser
 * does not hijack the pointer for panning / scrolling. The hook cannot set
 * this itself — it has no reference to the element's style.
 *
 * Unmount mid-drag releases pointer capture and detaches listeners but does
 * NOT call `onEnd` — consumers that need a final "commit" signal should also
 * commit on their own unmount effect.
 */
export const usePointerDrag = (
	args: UsePointerDragArgs,
): PointerDragHandlers => {
	const activeRef = useRef<ActiveDrag | null>(null);
	const argsRef = useRef(args);

	// Keep latest callbacks without re-binding listeners mid-drag. useLayoutEffect
	// so args are up-to-date before any pointerdown that fires in the same render
	// cycle (avoids a theoretical stale-closure race on the first move event).
	useLayoutEffect(() => {
		argsRef.current = args;
	});

	const cleanup = useCallback(() => {
		const active = activeRef.current;
		if (!active) return;
		const { target, pointerId, onMove, onEnd } = active;
		target.removeEventListener("pointermove", onMove);
		target.removeEventListener("pointerup", onEnd);
		target.removeEventListener("pointercancel", onEnd);
		try {
			target.releasePointerCapture(pointerId);
		} catch {
			// already released / unsupported — ignore.
		}
		activeRef.current = null;
	}, []);

	const onPointerDown = useCallback(
		(e: ReactPointerEvent) => {
			// If a previous drag is somehow still active (e.g. missed pointerup),
			// tear it down before starting a new one.
			if (activeRef.current) cleanup();
			if (argsRef.current.onStart?.(e) === false) return;

			const target = e.currentTarget as HTMLElement;
			const pointerId = e.pointerId;

			// setPointerCapture on currentTarget (the element the handler is bound
			// to), NOT e.target (which may be a descendant).
			target.setPointerCapture(pointerId);

			const onMove = (evt: PointerEvent) => {
				if (evt.pointerId !== pointerId) return;
				argsRef.current.onMove(evt);
			};

			const onEnd = (evt: PointerEvent) => {
				if (evt.pointerId !== pointerId) return;
				try {
					argsRef.current.onEnd?.(evt);
				} finally {
					cleanup();
				}
			};

			activeRef.current = { pointerId, target, onMove, onEnd };
			target.addEventListener("pointermove", onMove);
			target.addEventListener("pointerup", onEnd);
			target.addEventListener("pointercancel", onEnd);
		},
		[cleanup],
	);

	// Release capture + listeners on unmount if a drag is still active.
	useEffect(() => cleanup, [cleanup]);

	return { onPointerDown };
};
